import { computed } from 'vue'

import type { ComputedRef } from 'vue'
import type { NativeSelectOption } from '@shared/ui/fields'
import type { TutorKanbanStage } from '@brighterly/lib-core-types/tutor-kanban/enums'

import { useListsStore } from '@shared/model'

import { useTutorKanbanStore } from './store'
import { hasTutorDemoOnlyCategories } from '../config/tutor-kanban-demo-category.const'
import { hasTutorPaidCategories } from '../config/tutor-kanban-demo-category.const'

interface TutorKanbanSelectStageReturn {
  refOptions: ComputedRef<NativeSelectOption<TutorKanbanStage>[]>
  getInitialValue: () => TutorKanbanStage | null
  handleChange: (option: NativeSelectOption) => void
}

export const useTutorKanbanSelectStage = (): TutorKanbanSelectStageReturn => {
  const tutorKanbanStore = useTutorKanbanStore()
  const listsStore = useListsStore()

  const refList = computed<Record<string, string>>(() => listsStore.getLists?.tutor_stage?.all ?? {})

  const refCategories = computed<string[]>(() => {
    return (tutorKanbanStore.getTutor?.categories as string[] | null) ?? []
  })

  const refAllowedList = computed<Record<string, string>>(() => {
    const list = listsStore.getLists?.tutor_stage
    if (!list) return {}

    if (hasTutorDemoOnlyCategories(refCategories.value)) return list.demo ?? {}
    if (hasTutorPaidCategories(refCategories.value)) return list.paid ?? {}

    return list.all ?? {}
  })

  const getInitialValue = (): TutorKanbanStage | null => tutorKanbanStore.getTutor?.stage ?? null

  const refOptions = computed<NativeSelectOption<TutorKanbanStage>[]>(() => {
    const initStage = getInitialValue()

    return Object.entries(refList.value).map(([value, text]) => ({
      id: value,
      value: value as TutorKanbanStage,
      text,
      selected: initStage ? value === initStage : false,
      disabled: !(value in refAllowedList.value),
    }))
  })

  const handleChange = (option: NativeSelectOption): void => {
    tutorKanbanStore.setTutorStage(option.value as TutorKanbanStage)
  }

  return {
    refOptions,
    getInitialValue,
    handleChange,
  }
}
